// Generic per-index table: one row per measure, two columns —
//   [name + (i) explainer] | [absolute level, with the change in brackets].
//
// Shared by the Inequality dimension (#259) and, next, Poverty (#260, "align
// with #259"). Each row carries its own `goodWhen` so the change colour is
// per-measure (a rising bottom-decile income share is *good*, while a rising
// Gini is *bad*) — one shared dimension direction wouldn't fit.
//
// No %-change bar: inequality measures move by sub-percent amounts, so a bar on
// any honest shared scale is invisible, and the coloured level+Δ already carries
// both magnitude and direction. In the baseline (unmodified) view the bracket is
// dropped entirely — just the level.

import { InfoIcon } from "../info_icon";
import { deltaTextColour, type Dimension } from "../../lib/dimensions";

const NEUTRAL_TEXT_COLOUR = "#6e6e70";

export interface IndexRow {
  /** Stable id for React keys and the row data-testid. */
  key: string;
  name: string;
  /** One-sentence definition, shown in the (i) tooltip. */
  description: string;
  /** null = "n/a": the measure isn't computable (e.g. a ratio whose lower
   *  quantile is <= 0 on the synthetic fixture's negative bottom incomes). */
  reform: number | null;
  baseline: number | null;
  /** Which direction reads as the "good" move, per measure. "neutral" for a
   *  measure with no good/bad direction (e.g. the poverty line) — its change is
   *  shown but never coloured. */
  goodWhen: Dimension["goodWhen"] | "neutral";
  /** Absolute post-reform level for the value column (e.g. "0.282", "24.5%"). */
  formatLevel: (value: number) => string;
  /** Signed change in level units for the brackets (e.g. "−0.004", "+0.3 pp"). */
  formatDelta: (delta: number) => string;
}

export function IndexTable({
  rows,
  isModified,
  testId,
}: {
  rows: IndexRow[];
  isModified: boolean;
  testId: string;
}): JSX.Element {
  return (
    <div className="beamm-index-table" data-testid={testId}>
      {rows.map((row) => {
        const na = row.reform === null || row.baseline === null;
        const delta = na ? 0 : (row.reform as number) - (row.baseline as number);
        const showDelta = isModified && !na;
        const levelText = na ? "n/a" : row.formatLevel(row.reform as number);

        return (
          <div
            className="beamm-index-row"
            data-testid={`index-row-${row.key}`}
            key={row.key}
          >
            <span className="beamm-index-row__name">
              {row.name}
              <InfoIcon description={row.description} label={`${row.name} — what is it?`} />
            </span>
            <span
              className="beamm-index-row__level"
              data-testid={`index-row-${row.key}-level`}
            >
              <span className="beamm-index-row__level-value">{levelText}</span>
              {showDelta ? (
                <>
                  {" "}
                  <span
                    className="beamm-index-row__level-delta"
                    style={{
                      color:
                        delta === 0 || row.goodWhen === "neutral"
                          ? NEUTRAL_TEXT_COLOUR
                          : deltaTextColour(delta, row.goodWhen),
                    }}
                  >
                    ({row.formatDelta(delta)})
                  </span>
                </>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}
